// Template tags the email editors insert, in the Go text/template syntax the
// backend renders at send time (mail_api expand: .Contact.*, .SystemVars.*).
// Pure, so it's unit-testable.

// The double opt-in confirm link. Clicking it subscribes them (and, if the
// campaign has a reward, hands it over).
export const CONFIRM_URL_TAG = "{{ .SystemVars.ConfirmUrl }}";
export const DEFAULT_CONFIRM_LABEL = "Confirm my subscription";

// Easy Email's merge-tag menu: a tree whose dotted paths (Contact.Name) become
// {{ .Contact.Name }} via mergeTagGenerate. Matches the fields the sender
// exposes; values are just placeholders for the menu.
export const MERGE_TAGS = {
  Contact: { Name: "", Email: "", City: "", State: "", Country: "" },
};
export const mergeTagGenerate = (path) => `{{ .${path} }}`;

// Payload for Easy Email's Button block, pre-wired to the confirm link. Dragged
// in from the palette, it can be restyled/relabelled like any button.
export function confirmButtonPayload(label = DEFAULT_CONFIRM_LABEL) {
  return {
    attributes: {
      href: CONFIRM_URL_TAG,
      "background-color": "#0d6db8",
      color: "#ffffff",
      "font-size": "16px",
      "font-weight": "600",
      "border-radius": "6px",
      "inner-padding": "12px 28px",
      align: "center",
    },
    data: { value: { content: label } },
  };
}

// The same button as plain HTML, for the code editor.
export function confirmButtonHtml(label = DEFAULT_CONFIRM_LABEL) {
  return (
    `<a href="${CONFIRM_URL_TAG}" style="display:inline-block;padding:12px 28px;background:#0d6db8;color:#ffffff;` +
    `font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:16px;font-weight:600;text-decoration:none;border-radius:6px;">` +
    `${label}</a>`
  );
}

// Does this email content contain a working confirm link? (Either the plain
// confirm URL or the confirm-and-click variant counts.) Accepts the html,
// subject/text too — the check is on the template source.
export function hasConfirmLink(...parts) {
  return parts.some((p) => /\{\{\s*\.SystemVars\.Confirm(Url|AndClickLink)\b/.test(p || ""));
}

// Sample values the editor's preview shows in place of the contact merge tags.
export const PREVIEW_CONTACT = { Name: "Jane Doe", Email: "jane@example.com", City: "Austin", State: "TX", Country: "US" };

// A link with no real destination while editing: a template tag (the confirm
// button's {{ .SystemVars.ConfirmUrl }} only becomes a real address per
// recipient at send time), or Easy Email's placeholder "#", or nothing.
const isPlaceholderHref = (href) => {
  const h = (href || "").trim();
  return h === "" || h === "#" || h.includes("{{");
};

const hrefOf = (attrs) => {
  const m = /\bhref\s*=\s*("([^"]*)"|'([^']*)')/i.exec(attrs);
  return m ? (m[2] !== undefined ? m[2] : m[3]) : null;
};

// A campaign flow link: the confirm / unsubscribe / tracked links that only
// work per recipient at send time.
function isFlowLink(href) {
  const h = href.trim();
  return (
    /^\{\{\s*\.SystemVars\.(ConfirmUrl|UnsubscribeUrl)\s*\}\}$/.test(h) ||
    /^\{\{\s*\.SystemVars\.ConfirmAndClickLink\s*\}\}\/[A-Za-z0-9_-]+$/.test(h)
  );
}

const stripLinkAttrs = (attrs) =>
  attrs
    .replace(/\s*\bhref\s*=\s*("[^"]*"|'[^']*')/i, "")
    .replace(/\s*\btarget\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/i, "");

// previewHtml readies send-time template HTML for the editor's preview.
// Placeholder links (template tags, Easy Email's "#", empty) lose their href
// and target, so clicking does nothing — inside the preview they'd otherwise
// resolve to the editor page itself. With `markFlowLinks`, the campaign's flow
// links (confirm / unsubscribe / tracked) are tagged data-jc-flow-link so the
// editor can offer Test drive when one is clicked. Contact merge tags show
// sample values.
export function previewHtml(html, markFlowLinks = false) {
  return String(html || "")
    .replace(/<a\b([^>]*)>/gi, (tag, attrs) => {
      const href = hrefOf(attrs);
      if (href === null || !isPlaceholderHref(href)) return tag;
      if (markFlowLinks && isFlowLink(href)) {
        return `<a${stripLinkAttrs(attrs)} data-jc-flow-link="1" title="Try this in Test drive">`;
      }
      const title = href.includes("{{") ? "Works in the real email" : "This button doesn't link anywhere yet";
      return `<a${stripLinkAttrs(attrs)} title="${title}">`;
    })
    .replace(/\{\{\s*\.Contact\.(Name|Email|City|State|Country)\s*\}\}/g, (_, field) => PREVIEW_CONTACT[field]);
}

// flowLinkFromEvent: the flow link (see previewHtml) a click landed on, if any.
// The preview renders in a shadow root, so it looks along the event's path.
export function flowLinkFromEvent(e) {
  const path = typeof e.composedPath === "function" ? e.composedPath() : [];
  return path.find((n) => n && n.getAttribute && n.getAttribute("data-jc-flow-link") === "1") || null;
}

// deadLinks lists the text of links/buttons in the email that don't go
// anywhere (href "#" or empty), for a save-time warning. Template links
// (confirm, unsubscribe) are fine: they're filled in at send time.
export function deadLinks(html) {
  const out = [];
  const re = /<a\b([^>]*)>([\s\S]*?)<\/a>/gi;
  let m;
  while ((m = re.exec(String(html || "")))) {
    const href = hrefOf(m[1]);
    if (href === null || href.trim() === "" || href.trim() === "#") {
      const label = m[2].replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
      out.push(label || "(a link with no text)");
    }
  }
  return out;
}
